import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk'
import React, { createContext, useContext, useState } from 'react'
import { useClientOptions, useUser } from '@paintscout/ui-business'
import type { ReactNode } from 'react'

/**
 * Manual-only flag context
 */
type Flags = Record<string, any>
interface ManualFlagsContextValue {
  flags: Flags
  setFlag: (key: string, value: any) => void
}
const ManualFlagsContext = createContext<ManualFlagsContextValue | undefined>(undefined)

/**
 * Manual provider
 */
interface ManualFlagsProviderProps {
  initialFlags?: Flags
  children: ReactNode
}
export const ManualFlagsProvider = ({ initialFlags = {}, children }: ManualFlagsProviderProps) => {
  const [flags, setFlags] = useState<Flags>(initialFlags)
  const setFlag = (key: string, value: any) => setFlags((prev) => ({ ...prev, [key]: value }))

  return <ManualFlagsContext.Provider value={{ flags, setFlag }}>{children}</ManualFlagsContext.Provider>
}

/**
 * Wrapper hook
 * - If inside ManualFlagsProvider, use manual flags
 * - Otherwise, fallback to LDProvider flags
 */
export const useFlags = (): Flags => {
  const manualContext = useContext(ManualFlagsContext)
  const { nonBilling, isTrial, isCrew, trialExpired, isMasquerading } = useUser()
  const { clientId: companyId } = useClientOptions()
  const ldFlags = useLDFlags()

  if (manualContext) return manualContext.flags

  const ignoreNonBillingForTheseCompanies = [
    'aojknajpbuhoeeio', // Nolan Painting
    'lysvflucxgvmbqrf', // Canada's Best Painters
    'bjvoxhpmaxlkuoec', // Moose Painting
    'ypmagjgptfrqevou', // PaintScout CRM-off test account
    'arkgohdxhjzhcyke' // BugBug CRM-off test account
  ]

  const isNonBilling = nonBilling && !ignoreNonBillingForTheseCompanies.includes(companyId)

  const shouldAlwaysShowCRM = (isNonBilling || (isTrial && !trialExpired)) && !isCrew && ldFlags.crmVisible

  return {
    ...ldFlags,
    crm: shouldAlwaysShowCRM || ldFlags.crm,
    crmVisible: ldFlags.crmVisible
  }
}

/**
 * Optional hook to set a manual flag dynamically - not tested or in use
 */
// export const useSetFlag = () => {
//   const manualContext = useContext(ManualFlagsContext)
//   if (!manualContext) {
//     throw new Error('useSetFlag must be used within a ManualFlagsProvider')
//   }
//   return manualContext.setFlag
// }
